import type { QuerygenConversationSummary } from "../types";

const buildUrl = (path: string): string => new URL(path, window.location.origin).toString();

const buildHeaders = (body: RequestInit["body"]): HeadersInit => {
  if (!body || body instanceof FormData) {
    return {};
  }

  return { "Content-Type": "application/json" };
};

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(buildUrl(path), {
    credentials: "include",
    ...init,
    headers: {
      ...buildHeaders(init.body),
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;

    try {
      const payload = await response.json();
      if (payload?.message) {
        message = payload.message;
      } else if (payload?.error) {
        message = payload.error;
      }
    } catch {
      // Ignore JSON parsing errors for non-JSON responses.
    }

    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const querygenApi = {
  listRecentConversations: (agentId?: string, limit = 50) => {
    const url = new URL(buildUrl("/api/querygen/conversations/recent"));
    if (agentId) {
      url.searchParams.set("agentId", agentId);
    }
    url.searchParams.set("limit", String(limit));

    return fetch(url.toString(), {
      credentials: "include",
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const payload = (await response.json()) as {
        conversations: QuerygenConversationSummary[];
      };
      return payload.conversations ?? [];
    });
  },
  getConversationMessages: (
    conversationId: string,
  ): Promise<
    | {
        messages: Array<{
          id: string;
          conversation_id: string;
          role: string;
          content: { text: string; debug_information?: string };
          metadata?: Record<string, unknown> | null;
          timestamp: number;
        }>;
      }
    | Array<{
      id: string;
      conversation_id: string;
      role: string;
      content: { text: string; debug_information?: string };
      metadata?: Record<string, unknown> | null;
      timestamp: number;
    }>
  > => apiRequest(`/api/querygen/conversations/${conversationId}/messages`),
  setConversationStatus: (conversationId: string, status: string) =>
    apiRequest(`/api/querygen/conversations/${conversationId}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),
  deleteConversation: (conversationId: string) =>
    apiRequest<{ success: boolean }>(
      `/api/querygen/conversations/${conversationId}`,
      {
        method: "DELETE",
      },
    ),
  executePanel: (input: {
    agentId: string;
    query: string;
  }): Promise<{
    query: string;
    rowCount: number;
    truncated: boolean;
    rows: Record<string, unknown>[];
  }> =>
    apiRequest("/api/querygen/panels/execute", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  sendMessage: async (input: {
    channelId?: string;
    agentId?: string;
    message: string;
    conversationId?: string;
    metadata?: Record<string, string>;
    forceNewConversation?: boolean;
  }) => {
    const response = await fetch(buildUrl("/api/querygen/messages"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      let message = `${response.status} ${response.statusText}`;

      try {
        const payload = await response.json();
        if (payload?.message) {
          message = payload.message;
        } else if (payload?.error) {
          message = payload.error;
        }
      } catch {
        // Ignore parsing errors for non-JSON responses.
      }

      throw new Error(message);
    }

    return response;
  },
};
